import React, { useState, useEffect } from "react";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useDispatch } from "react-redux";
import {
    SG_GET_DEBRIEF_SCHEDULES,
    DEBRIEF_SCHEDULE_PAGE_NUMBER,
    DEBRIEF_SCHEDULE_SEARCH
} from "constants/actions";

// shadcn/ui components
import { Button } from "components/ui/button";
import { Alert, AlertDescription } from "components/ui/alert";
import { Card, CardContent } from "components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "components/ui/select";
import { Label } from "components/ui/label";

// Lucide icons
import { AlertTriangle, Calendar, Search as SearchIcon } from "lucide-react";

const searchOptions = [
    {
        value: 'start_dates',
        label: 'Start Date Between',
    },
    {
        value: 'close_dates',
        label: 'Close Date Between',
    },
];

const SearchList = ({ 
    organizationId, 
    setLoading,
    startDate, 
    setStartDate,
    endDate, 
    setEndDate,
    searchFor, 
    setSearchFor
}) => {
    const dispatch = useDispatch();
    const [errMsg, setErrMsg] = useState(false);
    const [page, setPage] = React.useState(1);

    function handleSearchFor(v) {
        setSearchFor(false);
        if (v) {
            setSearchFor(v);
        } else {
            dispatch({
                type: DEBRIEF_SCHEDULE_SEARCH,
                payload: {
                    search_for: false,
                    from: startDate,
                    to: endDate
                },
            });
        }
    }

    useEffect(() => {
        if (searchFor) {
            dispatch({
                type: DEBRIEF_SCHEDULE_SEARCH,
                payload: {
                    search_for: searchFor,
                    from: startDate,
                    to: endDate
                },
            });
        }
    }, [dispatch, searchFor, startDate, endDate]);

    function handleSubmit() {
        if (searchFor && startDate && endDate) {
            setLoading(true);
            setErrMsg(false);
            const _from = startDate.toISOString();
            const _to = endDate.toISOString();
            setPage(1);
            dispatch({
                type: DEBRIEF_SCHEDULE_PAGE_NUMBER,
                payload: 1,
            });
            dispatch({
                type: DEBRIEF_SCHEDULE_SEARCH,
                payload: {
                    search_for: searchFor,
                    from: startDate,
                    to: endDate
                },
            });
            dispatch({
                type: SG_GET_DEBRIEF_SCHEDULES,
                payload: `page=${page}&${searchFor}=true&from=${_from}&to=${_to}${organizationId ? `&organization=${organizationId}` : ''}`,
            });
        } else {
            setErrMsg(true);
        }
    }

    return (
        <Card>
            <CardContent className="p-6">
                {errMsg && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            You need to select Search For, Start Date and End date
                        </AlertDescription>
                    </Alert>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="search-for">Search For</Label>
                        <Select
                            value={searchFor || ""}
                            onValueChange={handleSearchFor}
                        >
                            <SelectTrigger id="search-for">
                                <SelectValue placeholder="Search for" />
                            </SelectTrigger>
                            <SelectContent>
                                {searchOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="start-date">Start Date</Label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                            <DatePicker
                                id="start-date"
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                wrapperClassName="w-full"
                                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                dateFormat="MMM dd, yyyy"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="end-date">End Date</Label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                            <DatePicker
                                id="end-date"
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                wrapperClassName="w-full"
                                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                dateFormat="MMM dd, yyyy"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>&nbsp;</Label>
                        <Button
                            onClick={handleSubmit}
                            className="w-full gap-2"
                            variant={searchFor ? "default" : "secondary"}
                        >
                            <SearchIcon className="h-4 w-4" />
                            Show
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default SearchList;
